import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { PageHeader, StatCard } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Users, TrendingDown } from "lucide-react";

const BUCKET_LABEL = { current: "Current", "30_60": "30–60", "60_90": "60–90", "90_plus": "90+" };
const BUCKET_COLOR = {
  current: "border-emerald-500/40 text-emerald-300 bg-emerald-500/10",
  "30_60": "border-yellow-500/40 text-yellow-300 bg-yellow-500/10",
  "60_90": "border-orange-500/40 text-orange-300 bg-orange-500/10",
  "90_plus": "border-rose-500/40 text-rose-300 bg-rose-500/10",
};

export default function DelinquencyPage() {
  const { propertyId } = useProperty();
  const [data, setData] = useState({ items: [], buckets: {} });
  useEffect(() => {
    if (!propertyId) return;
    (async () => {
      const { data } = await api.get(`/delinquency?property_id=${propertyId}`);
      setData(data);
    })();
  }, [propertyId]);

  const total = data.items.reduce((a, b) => a + Math.abs(b.balance || 0), 0);
  const past = data.items.filter((i) => i.aging_bucket !== "current").length;

  return (
    <div>
      <PageHeader eyebrow="Financial" title="Delinquency Pipeline" subtitle="Aging buckets across the community." />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard label="Total owed" value={"$" + total.toLocaleString()} icon={TrendingDown} accent="red" testid="del-total" />
        <StatCard label="Accounts past-due" value={past} icon={Users} accent="orange" />
        {Object.entries(BUCKET_LABEL).slice(1).map(([b, l]) => (
          <StatCard
            key={b}
            label={`${l} days`}
            value={(data.buckets[b] || []).length}
            hint={"$" + (data.buckets[b] || []).reduce((a, c) => a + Math.abs(c.balance || 0), 0).toLocaleString()}
          />
        ))}
      </div>
      <Card className="border-white/10 bg-card overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-white/10">
              <TableHead>Resident</TableHead>
              <TableHead>Unit</TableHead>
              <TableHead className="text-right">Balance</TableHead>
              <TableHead className="text-right">Days past due</TableHead>
              <TableHead>Bucket</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.items.map((l) => (
              <TableRow key={l.id} className="border-white/5" data-testid={`del-row-${l.id}`}>
                <TableCell className="font-medium">{l.resident_name}</TableCell>
                <TableCell>{l.unit}</TableCell>
                <TableCell className={"text-right tabular-nums " + (l.balance < 0 ? "text-rose-400" : "text-emerald-400")}>
                  ${Math.abs(l.balance || 0).toLocaleString()}
                </TableCell>
                <TableCell className="text-right">{l.days_past_due}</TableCell>
                <TableCell>
                  <Badge variant="outline" className={BUCKET_COLOR[l.aging_bucket]}>
                    {BUCKET_LABEL[l.aging_bucket] || l.aging_bucket}
                  </Badge>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
